import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

function timeSince(date) {
  if (!date) return 'never';
  const sec = Math.floor((Date.now() - new Date(date)) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function Devices({ user }) {
  const [devices,       setDevices]       = useState([]);
  const [patches,       setPatches]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [assigning,     setAssigning]     = useState(null);
  const [selectedPatch, setSelectedPatch] = useState({});
  const [msg,           setMsg]           = useState({ text: '', type: '' });
  const [statusFilter,  setStatusFilter]  = useState('all'); // 'all' | 'online' | 'offline'

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([api.getDevices(), api.getAllPatches()]);
      setDevices(d.data);
      setPatches(p.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function assign(deviceId) {
    if (!isAdmin) return;
    const patchId = selectedPatch[deviceId];
    if (!patchId) return;
    setAssigning(deviceId);
    try {
      await api.assignPatch(deviceId, patchId);
      setMsg({ text: '✔ Patch assigned. Agent will deploy on next poll (within 10 min).', type: 'success' });
      setTimeout(() => setMsg({ text: '', type: '' }), 5000);
    } catch (e) {
      setMsg({ text: 'Failed to assign patch: ' + (e.response?.data?.error || e.message), type: 'error' });
    } finally {
      setAssigning(null);
    }
  }

  const online  = devices.filter(d => d.status === 'online').length;
  const offline = devices.filter(d => d.status !== 'online').length;
  const visibleDevices = statusFilter === 'all' ? devices
    : statusFilter === 'online'  ? devices.filter(d => d.status === 'online')
    : devices.filter(d => d.status !== 'online');

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Devices</div>
          <div className="page-subtitle">All registered endpoints</div>
        </div>
        <button className="refresh-btn" onClick={load}>↻ Refresh</button>
      </div>

      <div className="cards-row">
        <div className="card blue" style={{ cursor:'pointer' }} onClick={() => setStatusFilter('all')}>
          <div className="card-label">Total</div>
          <div className="card-value">{devices.length}</div>
          <div className="card-sub">{statusFilter==='all' ? '▼ showing all' : 'click to show all'}</div>
        </div>
        <div className="card green" style={{ cursor:'pointer', outline: statusFilter==='online'?'2px solid #16a34a':undefined }} onClick={() => setStatusFilter(statusFilter==='online'?'all':'online')}>
          <div className="card-label">Online</div>
          <div className="card-value">{online}</div>
          <div className="card-sub">{statusFilter==='online' ? '▼ filtered' : 'click to filter'}</div>
        </div>
        <div className="card red" style={{ cursor:'pointer', outline: statusFilter==='offline'?'2px solid #dc2626':undefined }} onClick={() => setStatusFilter(statusFilter==='offline'?'all':'offline')}>
          <div className="card-label">Offline</div>
          <div className="card-value">{offline}</div>
          <div className="card-sub">{statusFilter==='offline' ? '▼ filtered' : 'click to filter'}</div>
        </div>
      </div>

      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {msg.text}
        </div>
      )}

      {/* Non-admin notice */}
      {!isAdmin && (
        <div className="alert info" style={{ marginBottom: 16 }}>
          🔒 You have read-only access. Only <strong>Admin</strong> can deploy patches.
          Submit a request via <strong>Settings → Approval Matrix</strong>.
        </div>
      )}

      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">
            <div className="panel-title-icon">⊡</div>
            Device List
          </div>
        </div>
        {loading ? <div className="loading">Loading...</div> : (
          <table>
            <thead>
              <tr>
                <th>Hostname</th>
                <th>OS</th>
                <th>Hardware</th>
                <th>IP Address</th>
                <th>Status</th>
                <th>Last Heartbeat</th>
                {/* Only show Assign Patch column to admin */}
                {isAdmin && <th>Assign Patch</th>}
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="empty">
                  No devices registered yet.
                </td></tr>
              )}
              {visibleDevices.map(d => (
                <tr key={d.id}>
                  <td><strong>{d.hostname}</strong></td>
                  <td>
                    <div style={{ fontSize: 13 }}>{d.os}</div>
                    {d.osVersion && (
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{d.osVersion}</div>
                    )}
                  </td>
                  <td>
                    {d.hwManufacturer ? (
                      <div>
                        <div style={{ fontSize: 13 }}>{d.hwManufacturer} {d.hwModel}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          {d.isDell ? '🔵 Dell' : d.isHP ? '🔵 HP' : d.isSurface ? '🔵 Surface' : d.isLenovo ? '🔵 Lenovo' : '⚪ Generic'}
                          {d.biosVersion ? ` · BIOS ${d.biosVersion}` : ''}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>Not detected yet</span>
                    )}
                  </td>
                  <td style={{ fontSize: 13 }}>{d.ipAddress || '—'}</td>
                  <td>
                    <span className={`badge ${d.status === 'online' ? 'online' : 'offline'}`}>
                      ● {d.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#94a3b8' }}>
                    {timeSince(d.lastHeartbeat)}
                  </td>

                  {/* Deploy — admin only */}
                  {isAdmin && (
                    <td>
                      <div className="flex-gap">
                        <select
                          className="form-input"
                          style={{ padding: '5px 8px', fontSize: 12, maxWidth: 240 }}
                          value={selectedPatch[d.id] || ''}
                          onChange={e => setSelectedPatch(p => ({ ...p, [d.id]: e.target.value }))}
                        >
                          <option value="">Select patch...</option>
                          {patches
                            .filter(p => {
                              // Must match OS type
                              if (p.osType !== d.os) return false;
                              // Hardware vendor filtering — only show vendor-specific patches
                              // for the correct vendor, show generic (non-vendor) patches to all
                              const vendor = p.vendor?.toLowerCase() || '';
                              const isDellPatch = vendor === 'dell' || (p.product || '').toLowerCase().includes('dell');
                              const isHPPatch   = vendor === 'hp'   || (p.product || '').toLowerCase().includes('hp');
                              const isSurfacePatch = vendor === 'microsoft' && (p.category === 'firmware' || p.product?.toLowerCase().includes('surface'));
                              const isLenovoPatch  = vendor === 'lenovo' || (p.product || '').toLowerCase().includes('lenovo');
                              // If the patch is vendor-specific, only show it if device matches that vendor
                              if (isDellPatch    && !d.isDell)    return false;
                              if (isHPPatch      && !d.isHP)      return false;
                              if (isSurfacePatch && !d.isSurface) return false;
                              if (isLenovoPatch  && !d.isLenovo)  return false;
                              return true;
                            })
                            .map(p => (
                              <option key={p.id} value={p.id}>
                                {p.vendor} — {p.product} v{p.version}
                              </option>
                            ))
                          }
                        </select>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={!selectedPatch[d.id] || assigning === d.id}
                          onClick={() => assign(d.id)}
                        >
                          {assigning === d.id ? '⟳' : 'Deploy'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
